import { useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Icon } from "./components/Icon";
import { GOD_NAV, GOD_CRUMB, OPERATOR } from "./data/nav";
import { GodDash } from "./pages/Dashboard";
import { Stub } from "./pages/Stub";
import { UsersPage } from "./pages/Users";
import { OrgsPage } from "./pages/Orgs";
import { CostPage } from "./pages/Cost";
import { CurriculumGod } from "./pages/Curriculum";
import { RevenuePage, AIPage, MemoryPage, ObservabilityPage, ReleasesPage, FlagsPage, CommsPage, AuditPage } from "./pages/more";
import { useSuperAdmin, login, logout } from "./lib/api";
import { useUsers, useOrgs } from "./lib/admin";
import { useToasts, dismissToast } from "./lib/toast";
import type { Imp, SetImp } from "./types";
import type { ReactElement } from "react";

function pagesFor(setImp: SetImp): Record<string, ReactElement> {
  return {
    dash: <GodDash />,
    users: <UsersPage setImp={setImp} />,
    orgs: <OrgsPage />,
    revenue: <RevenuePage />,
    cost: <CostPage />,
    curriculum: <CurriculumGod />,
    ai: <AIPage />,
    memory: <MemoryPage />,
    observability: <ObservabilityPage />,
    releases: <ReleasesPage />,
    flags: <FlagsPage />,
    comms: <CommsPage />,
    audit: <AuditPage />,
  };
}

const compact = (n: number): string => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "") + "k" : String(n));

function GodSidebar() {
  // Live counts for the People group; fall back to the static nav count until loaded.
  const users = useUsers();
  const orgs = useOrgs();
  const liveCount: Record<string, string | number | undefined> = {
    users: users ? compact(users.users.length) : undefined,
    orgs: orgs ? orgs.orgs.length : undefined,
  };
  return (
    <aside className="side">
      <div className="side-top">
        <div className="brand">
          <span className="bmark god"><Icon name="shield" size={16} color="#fff" /></span>
          <span className="bn">
            Adaptr <span className="god-tag">GOD</span>
            <small>super-admin console</small>
          </span>
        </div>
      </div>
      <nav className="nav">
        {GOD_NAV.map((grp) => (
          <div key={grp.group}>
            <div className="nav-h">{grp.group}</div>
            {grp.items.map((it) => (
              <NavLink key={it.id} to={`/${it.id}`} className={({ isActive }) => `nav-i ${isActive ? "on" : ""}`}>
                <Icon name={it.icon} size={17} className="nav-ic" />
                <span className="nt">{it.label}</span>
                {(liveCount[it.id] ?? it.count) != null && <span className="cnt">{liveCount[it.id] ?? it.count}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="side-foot">
        <div className="user-chip">
          <span className="op-badge"><Icon name="venetian-mask" size={15} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="un">{OPERATOR.email}</div>
            <div className="um">super_admin · all access</div>
          </div>
          <span className="sdot ok" />
        </div>
      </div>
    </aside>
  );
}

function GodTopbar() {
  const { pathname } = useLocation();
  const route = pathname.replace(/^\//, "") || "dash";
  const crumb = GOD_CRUMB[route] || ["", ""];
  return (
    <header className="topbar">
      <div className="crumb">
        <span>{crumb[0]}</span>
        <Icon name="chevron-right" size={15} className="sl" />
        <b>{crumb[1]}</b>
      </div>
      <div className="env-pill"><span className="sdot ok" />production</div>
      <div className="tb-right">
        <button className="tb-btn"><Icon name="search" size={18} /></button>
        <button className="tb-btn"><Icon name="bell" size={18} /><span className="tb-dot" /></button>
        <div className="tb-div" />
        <button className="kill-btn"><Icon name="power" size={15} strokeWidth={2.2} />Kill switch</button>
      </div>
    </header>
  );
}

function ImpBanner({ imp, onExit }: { imp: Imp; onExit: () => void }) {
  return (
    <div className="imp-banner">
      <span className="imp-ic"><Icon name="venetian-mask" size={16} /></span>
      <span className="imp-text">Impersonating <b>{imp.email}</b> — read &amp; write. Every action is logged.</span>
      <span className="imp-timer"><Icon name="circle" size={8} />session active</span>
      <button className="imp-exit" onClick={onExit}><Icon name="log-out" size={14} />Exit session</button>
    </div>
  );
}

function Gate({ children }: { children: ReactElement }) {
  const auth = useSuperAdmin();
  if (auth.status === "ok") return children;
  return (
    <div className="god-gate">
      <span className="gate-mark"><Icon name="shield" size={26} color="#fff" /></span>
      {auth.status === "loading" && (
        <>
          <div className="gate-t">Verifying operator…</div>
          <div className="gate-d">Checking super-admin access.</div>
        </>
      )}
      {auth.status === "anon" && (
        <>
          <div className="gate-t">Adaptr <span className="god-tag">GOD</span></div>
          <div className="gate-d">Super-admin console. Operator sign-in required.</div>
          <button className="kill-btn gate-btn" onClick={login}><Icon name="venetian-mask" size={15} />Operator sign in</button>
        </>
      )}
      {auth.status === "forbidden" && (
        <>
          <div className="gate-t">Not authorized</div>
          <div className="gate-d">This account isn’t a platform super-admin.</div>
          <button className="kill-btn gate-btn" onClick={logout}><Icon name="log-out" size={15} />Sign out</button>
        </>
      )}
    </div>
  );
}

export default function GodApp() {
  // Impersonation lifts to the shell so the banner spans every section.
  const [imp, setImp] = useState<Imp | null>(null);
  const pages = pagesFor(setImp);
  return (
    <Gate>
      <div className="browser god">
        <div className="app">
          <GodSidebar />
          <main className="main">
            {imp && <ImpBanner imp={imp} onExit={() => setImp(null)} />}
            <GodTopbar />
            <div className="content">
              <Routes>
                <Route path="/" element={<Navigate to="/dash" replace />} />
                {GOD_NAV.flatMap((g) => g.items).map((it) => (
                  <Route key={it.id} path={`/${it.id}`} element={pages[it.id] ?? <Stub label={it.label} />} />
                ))}
                <Route path="*" element={<Navigate to="/dash" replace />} />
              </Routes>
            </div>
          </main>
        </div>
        <Toaster />
      </div>
    </Gate>
  );
}

function Toaster() {
  const toasts = useToasts();
  if (toasts.length === 0) return null;
  return (
    <div className="toaster">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`} onClick={() => dismissToast(t.id)}>
          <Icon name={t.kind === "success" ? "check" : t.kind === "error" ? "alert-triangle" : "info"} size={15} />
          <span>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}
